import React, { useEffect, useState } from 'react';
import { api, DisclosureItem, LegalDocumentItem } from '../../services/api';
import { Card, Btn, C } from '../../components/UI';
import { useBranding } from '../../context/BrandingContext';

// ── Contractor Disclosures — legal agreement text admin editor ─────────────────
// The backend (DisclosureService.UpdateDisclosureAsync) already bumps Version and resets
// DisclosuresComplete for any contractor who'd previously accepted the changed disclosure,
// forcing re-acceptance — exactly right for a material change like a legal entity name. This
// panel was the missing piece: the update endpoint and API client method already existed with
// no UI ever calling them.
//
// Site Legal Pages (Privacy Policy / Terms of Service) live in the same view below — same
// "admin can edit the legal text" need, but no acceptance tracking since they're shown to
// every visitor, not accepted by a specific contractor. Separate backend model/table
// (LegalDocument, not ContractorDisclosure) since the audience and lifecycle differ.

export function DisclosurePanel() {
    return (
        <div style={{ maxWidth: 900 }}>
            <ContractorDisclosuresSection />
            <div style={{ height: 1, background: C.border, margin: '32px 0' }} />
            <LegalDocumentsSection />
        </div>
    );
}

function ContractorDisclosuresSection() {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelLower = contractorLabel.toLowerCase();
    const [disclosures, setDisclosures] = useState<DisclosureItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editBody, setEditBody] = useState('');
    const [saving, setSaving] = useState(false);
    const [successId, setSuccessId] = useState<string | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.adminGetDisclosures().then(setDisclosures).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const startEdit = (d: DisclosureItem) => {
        setEditingId(d.id);
        setEditTitle(d.title);
        setEditBody(d.body);
        setSuccessId(null);
        setError('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditTitle('');
        setEditBody('');
    };

    const save = async (id: string) => {
        if (!editTitle.trim() || !editBody.trim()) { setError('Title and body are both required.'); return; }
        setSaving(true); setError('');
        try {
            const updated = await api.adminUpdateDisclosure(id, editTitle.trim(), editBody.trim());
            setDisclosures(prev => prev.map(d => d.id === id ? updated : d));
            setEditingId(null);
            setSuccessId(id);
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{contractorLabel} Disclosures</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textSub }}>
                    Legal agreements and disclosures shown to {contractorLabelLower}s during onboarding. Editing a disclosure
                    increments its version and requires any {contractorLabelLower} who already accepted it to accept the new
                    version before they can continue working — use this for real, material changes, not typos.
                </p>
            </div>

            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {disclosures.map(d => (
                        <Card key={d.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <strong style={{ fontSize: 14 }}>{d.title}</strong>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, background: C.bg, borderRadius: 12, padding: '2px 8px' }}>v{d.version}</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                                        {d.type} · updated {new Date(d.updatedAt).toLocaleDateString()}
                                    </div>
                                </div>
                                {editingId !== d.id && (
                                    <Btn size="sm" variant="outline" onClick={() => startEdit(d)}>Edit</Btn>
                                )}
                            </div>

                            {successId === d.id && (
                                <div style={{ marginTop: 10, padding: '8px 12px', background: C.greenBg, borderRadius: 8, fontSize: 12, color: C.green }}>
                                    ✓ Saved as version {d.version}. Any contractor who'd already accepted the previous version will be asked to re-accept.
                                </div>
                            )}

                            {editingId === d.id ? (
                                <div style={{ marginTop: 12 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Title</label>
                                    <input
                                        value={editTitle}
                                        onChange={e => setEditTitle(e.target.value)}
                                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 10 }}
                                    />
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Body</label>
                                    <textarea
                                        value={editBody}
                                        onChange={e => setEditBody(e.target.value)}
                                        rows={10}
                                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', marginBottom: 12 }}
                                    />
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Btn size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>Cancel</Btn>
                                        <Btn size="sm" onClick={() => save(d.id)} disabled={saving}>{saving ? 'Saving…' : 'Save (creates a new version)'}</Btn>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: C.textSub, whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'hidden' }}>
                                    {d.body}
                                </p>
                            )}
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Site Legal Pages — Privacy Policy / Terms of Service admin editor ──────────
// Backs the public /privacy and /terms pages (PrivacyPolicy.tsx, TermsOfService.tsx), which
// fetch from GET /api/legal/{type} and fall back to their own hardcoded copy if this tenant
// hasn't customized it yet. No version/acceptance tracking — these are viewed, not "accepted."
function LegalDocumentsSection() {
    const [docs, setDocs] = useState<LegalDocumentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editBody, setEditBody] = useState('');
    const [saving, setSaving] = useState(false);
    const [successId, setSuccessId] = useState<string | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.adminGetLegalDocuments().then(setDocs).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const startEdit = (d: LegalDocumentItem) => {
        setEditingId(d.id);
        setEditBody(d.body);
        setSuccessId(null);
        setError('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditBody('');
    };

    const save = async (d: LegalDocumentItem) => {
        if (!editBody.trim()) { setError('Body is required.'); return; }
        setSaving(true); setError('');
        try {
            const updated = await api.adminUpdateLegalDocument(d.id, d.title, editBody.trim());
            setDocs(prev => prev.map(x => x.id === d.id ? updated : x));
            setEditingId(null);
            setSuccessId(d.id);
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Site Legal Pages</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textSub }}>
                    The Privacy Policy and Terms of Service shown to every visitor (linked from signup and checkout).
                    Edited here as plain text — format it with blank lines and dashes as you'd like it to read.
                </p>
            </div>

            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {docs.map(d => (
                        <Card key={d.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <strong style={{ fontSize: 14 }}>{d.title}</strong>
                                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                                        updated {new Date(d.updatedAt).toLocaleDateString()}
                                    </div>
                                </div>
                                {editingId !== d.id && (
                                    <Btn size="sm" variant="outline" onClick={() => startEdit(d)}>Edit</Btn>
                                )}
                            </div>

                            {successId === d.id && (
                                <div style={{ marginTop: 10, padding: '8px 12px', background: C.greenBg, borderRadius: 8, fontSize: 12, color: C.green }}>
                                    ✓ Saved. The live page now shows this text.
                                </div>
                            )}

                            {editingId === d.id ? (
                                <div style={{ marginTop: 12 }}>
                                    <textarea
                                        value={editBody}
                                        onChange={e => setEditBody(e.target.value)}
                                        rows={16}
                                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', marginBottom: 12 }}
                                    />
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Btn size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>Cancel</Btn>
                                        <Btn size="sm" onClick={() => save(d)} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: C.textSub, whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'hidden' }}>
                                    {d.body}
                                </p>
                            )}
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
